import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
    addDays,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isFuture,
    isSameDay,
    isSameMonth,
    isToday,
    Locale,
    startOfMonth,
    startOfWeek,
} from 'date-fns'
import { FC, useMemo, useState } from 'react'
import { CalendarData } from '../../../../types/calendar'
import { CreateEventMenu } from './CreateEventMenu'
import { EventItem } from './EventItem'

const daysToRender = Array.from({ length: 7 }).map((_, index) => index)

type Props = {
    currentMonth: Date
    currentWeek: number
    selectedDate: Date
    onSelectDate: (newDate: Date) => void
    locale?: Locale
    data: CalendarData
}

export const MonthCalendar: FC<Props> = ({
    currentMonth,
    currentWeek,
    selectedDate,
    locale,
    onSelectDate,
    data,
}) => {
    const { breakpoints } = useTheme()

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const [menuOpen, setMenuOpen] = useState(false)
    const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 })
    const [createEventDate, setCreateEventDate] = useState<Date | undefined>(undefined)

    const handleClose = () => {
        setMenuOpen(false)
    }

    const firstDayOfMonth = startOfMonth(currentMonth)
    const lastDayOfMonth = endOfMonth(currentMonth)
    const startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 })
    const endDate = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 })

    const days = eachDayOfInterval({ start: startDate, end: endDate })

    const weeks = useMemo(
        () =>
            Array.from({
                length: days.length / 7,
            }).map((_, index) => index),
        [currentMonth],
    )

    const numberOfEventsToShow = isMobile ? 5 : 3

    return (
        <Stack
            sx={{
                height: '100%',
                width: '100%',
                p: 0,
            }}>
            <Stack
                direction="row"
                sx={{
                    width: '100%',
                    p: 0,
                    borderBottom: '1px solid #F2F2F2',
                }}>
                {daysToRender.map((dayIndex) => {
                    const weekDayFormat = 'eeeeee'
                    const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 })
                    const day = addDays(startDate, dayIndex)

                    return (
                        <Stack
                            key={`day-header-${day.toISOString()}`}
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                height: '100%',
                                width: '100%',
                                gap: 0.5,
                                maxWidth: 'calc(100% / 7)',
                                p: isMobile ? 0.5 : 2,
                            }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    textTransform: 'capitalize',
                                }}>
                                {format(day, weekDayFormat, { locale })}
                            </Typography>
                        </Stack>
                    )
                })}
            </Stack>
            <Stack
                direction="column"
                sx={{
                    height: '100%',
                    width: '100%',
                    p: 0,
                    '& > div:not(:last-of-type)': {
                        borderBottom: '1px solid #F2F2F2',
                    },
                }}>
                {weeks.map((weekIndex) => {
                    return (
                        <Stack
                            key={`week-${weekIndex}`}
                            direction="row"
                            sx={{
                                height: '100%',
                                width: '100%',
                                p: 0,

                                '& > div:not(:last-of-type)': {
                                    borderRight: '1px solid #F2F2F2',
                                },
                            }}>
                            {daysToRender.map((dayIndex) => {
                                const dateFormat = 'd'
                                const dayIdx = dayIndex + weekIndex * daysToRender.length

                                const day = days.at(dayIdx)!

                                const isSelected = isSameDay(day, selectedDate)
                                const isFirstDayOfMonts = isSameDay(day, startOfMonth(day))
                                const isCurrentDay = isToday(day)
                                const isCurrentMonth = isSameMonth(day, firstDayOfMonth)

                                const events = data[day.toISOString()] ?? []
                                const eventsToShow = events.slice(0, numberOfEventsToShow)

                                const hiddenEvents = events.length - numberOfEventsToShow

                                return (
                                    <Stack
                                        key={`dayCell-${weekIndex}_${dayIdx}_${day.toISOString()}`}
                                        direction="column"
                                        onClick={() => onSelectDate(day)}
                                        onContextMenu={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()

                                            onSelectDate(day)

                                            const canCreateEvent = isFuture(day) || isToday(day)

                                            if (canCreateEvent) {
                                                setMenuOpen(true)
                                                setAnchorPosition({
                                                    top: e.clientY,
                                                    left: e.clientX,
                                                })
                                                setCreateEventDate(day)
                                            }
                                        }}
                                        sx={{
                                            height: '100%',
                                            minHeight: '44px',
                                            width: '100%',
                                            maxWidth: 'calc(100% / 7)',
                                            p: '4px',
                                            gap: '4px',
                                            cursor: 'pointer',
                                            justifyContent: 'space-between',
                                            backgroundColor: isSelected ? '#EBF5FD' : '#FFFFFF',
                                            ':hover': {
                                                backgroundColor: isSelected ? '#EBF5FD' : '#F6F6F6',
                                            },
                                        }}>
                                        <Stack
                                            direction="column"
                                            sx={{
                                                width: '100%',
                                                gap: '4px',
                                            }}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                sx={{
                                                    width: '100%',
                                                    gap: '4px',
                                                    p: '4px',
                                                    alignItems: isMobile ? 'center' : 'start',
                                                    justifyContent: isMobile ? 'center' : 'start',
                                                }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: 'fit-content',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minHeight: '20px',
                                                        minWidth: isCurrentDay ? '20px' : 'auto',
                                                        borderRadius: '50%',
                                                        backgroundColor: isCurrentDay
                                                            ? '#006CFD'
                                                            : 'transparent',
                                                        color: isCurrentDay
                                                            ? 'white'
                                                            : isCurrentMonth
                                                              ? '#1B1C1D'
                                                              : '#5A5C5E',
                                                    }}>
                                                    {format(day, dateFormat, {
                                                        locale,
                                                    })}
                                                </Typography>
                                                {isFirstDayOfMonts && !isMobile && (
                                                    <Typography variant="body2">
                                                        {format(day, 'MMMM', {
                                                            locale,
                                                        })}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Stack
                                                sx={{
                                                    flexDirection: isMobile ? 'row' : 'column',
                                                    alignItems: isMobile ? 'center' : 'start',
                                                    justifyContent: isMobile ? 'center' : 'start',
                                                    width: '100%',
                                                    gap: '4px',
                                                }}>
                                                {eventsToShow.map((data, index) => {
                                                    const isRegularRequest = data && 'reply' in data

                                                    if (isMobile) {
                                                        return (
                                                            <Box
                                                                sx={{
                                                                    width: '4px',
                                                                    height: '4px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor:
                                                                        !isRegularRequest
                                                                            ? '#229F0A'
                                                                            : '#006CFD',
                                                                }}
                                                            />
                                                        )
                                                    }

                                                    return (
                                                        <EventItem
                                                            key={`event_${index}_${weekIndex}_${dayIndex}_${day.toISOString()}`}
                                                            variant="row"
                                                            popoverPosition="right"
                                                            lineClamp={1}
                                                            data={data}
                                                        />
                                                    )
                                                })}
                                            </Stack>
                                        </Stack>
                                        {hiddenEvents > 0 && !isMobile && (
                                            <Typography
                                                variant="Description"
                                                color="#006CFD"
                                                p="0 4px">
                                                Ещё {hiddenEvents}
                                            </Typography>
                                        )}
                                    </Stack>
                                )
                            })}
                        </Stack>
                    )
                })}
            </Stack>
            <CreateEventMenu
                selectedDate={createEventDate}
                open={menuOpen}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={{
                    top: anchorPosition.top,
                    left: anchorPosition.left,
                }}
            />
        </Stack>
    )
}
